export type ImagePreset =
  | "graphic" // raster image that features simplistic, perhaps vector-like art (q=60)
  | "photo" // photographs (q=70)
  | "hero" // hero images, where we bias towards visual quality (q=75)
  | "editorial" // editorial uploads (blog/article images), where we don't necessarily know the image content (q=75)

export type ImageFormat = "png" | "jpg" | "jpeg" | "webp" | "svg"
export type ImageFit =
  | "clip"
  | "clamp"
  | "crop"
  | "facearea"
  | "fill"
  | "fillmax"
  | "max"
  | "min"
  | "scale"

/**
 * Optimizes DatoCMS image URLs by adding appropriate Imgix parameters
 *
 * @param url The original DatoCMS image URL (enforced to have no query parameters)
 * @param options Configuration options
 * @returns Optimized image URL with Imgix parameters
 */
interface OptimizeImageOptions {
  /** Quality setting (1-100). Default varies by preset */
  quality?: number
  /** Type of image for auto-quality settings */
  preset?: ImagePreset
  /** Enable WebP/AVIF format conversion */
  autoFormat?: boolean
  /** Request smart compression from Imgix */
  autoCompress?: boolean
  /** Device Pixel Ratio (1-5) for responsive images */
  dpr?: number
  /** Resize fit mode (clip, clamp, crop, etc.) */
  fit?: ImageFit
}

// Generally, query string params are not supported by OptimizedImage.
// However, we support `dpr` and `fit` for backwards compatibility with
// DatoImage, which supports these params in the query string.
function extractSpecialSrcSetQueryParams(url: string) {
  const params: { dpr?: number; fit?: ImageFit } = {}

  if (!url || typeof url !== "string") {
    return params
  }

  try {
    const urlObj = new URL(url)
    const dpr = urlObj.searchParams.get("dpr")
    const fit = urlObj.searchParams.get("fit")

    if (dpr) {
      const dprValue = parseFloat(dpr)
      if (!Number.isNaN(dprValue)) {
        params.dpr = Math.round(dprValue)
      }
    }

    if (fit) {
      const validFitValues: ImageFit[] = [
        "clip",
        "clamp",
        "crop",
        "facearea",
        "fill",
        "fillmax",
        "max",
        "min",
        "scale",
      ]

      if (validFitValues.includes(fit as ImageFit)) {
        params.fit = fit as ImageFit
      }
    }

    return params
  } catch (e) {
    return params
  }
}

export function optimizeImageUrl(
  url: string,
  options: OptimizeImageOptions = {},
) {
  if (!url || !url.includes("datocms-assets.com")) {
    // eslint-disable-next-line no-console
    console.warn(`Invalid image URL provided to optimizeImageUrl: "${url}"`)
    return url || ""
  }

  // DatoCMS tends to corrupt SVGs
  // returns them as PNG (uglier, larger)
  // so we skip optimization
  if (url.endsWith(".svg")) {
    return url
  }

  let { quality } = options
  if (!quality) {
    switch (options.preset) {
      case "graphic":
        quality = 60
        break
      case "photo":
        quality = 70
        break
      // conservative quality setting for hero images,
      // or editorial (user upload by marketing)
      case "hero":
      case "editorial":
      default:
        quality = 75
        break
    }
  }

  const urlWithoutQueryString = url.split("?")[0]
  const params = new URLSearchParams()

  const autoFormat = options.autoFormat !== false
  const autoCompress = options.autoCompress !== false

  if (autoFormat || autoCompress) {
    const autoParams = []
    if (autoFormat) autoParams.push("format")
    if (autoCompress) autoParams.push("compress")
    params.set("auto", autoParams.join(","))
  }

  params.set("q", `${quality}`)

  // Extract parameters from source URL if present
  // → should be very rare; requires one to cast `imageUrl` as `DatoCmsImageUrl`
  const { dpr: querystringDpr, fit: querystringFit } =
    extractSpecialSrcSetQueryParams(url)

  // Device pixel radio (dpr) parameter
  // query string takes precedence over options
  const resolvedDpr = querystringDpr || options.dpr || 1
  if (resolvedDpr > 1) {
    const boundedDpr = Math.min(Math.max(resolvedDpr, 1), 5)
    params.set("dpr", `${boundedDpr}`)
  }

  // Image fit parameter
  // query string takes precedence over options
  const resolvedFit = querystringFit || options.fit
  if (resolvedFit) {
    params.set("fit", resolvedFit)
  }

  // preserve colorspace origin
  // otherwise, images may appear dull
  // Imgix's auto=compress algo will convert imagesto sRGB and overcompress
  // https://docs.imgix.com/en-US/apis/rendering/automatic (see cs=origin)
  params.set("cs", "origin")

  return `${urlWithoutQueryString}?${params.toString()}`
}
